<template>
  <div class="ski-schools-page">
    <!-- Header Section -->
    <div class="page-header">
      <h1>Ski Schools</h1>
      <p class="page-description">Discover water ski schools around the world</p>
    </div>

    <!-- Create Button Section -->
    <div v-if="isAuthenticated && user.verified" class="create-section">
      <button 
        @click="navigateToCreate" 
        class="create-school-button"
      >
        <i class="fas fa-plus"></i>
        Create Ski School
      </button>
    </div>

    <!-- Filters Section -->
    <div class="filters-section">
      <div class="filters-container">
        <div class="filter-group">
          <label for="search-input">Search:</label>
          <input 
            id="search-input"
            type="text"
            v-model="searchTerm"
            @input="filterSkiSchools"
            placeholder="Search ski schools..."
            class="search-input"
          />
        </div>
        
        <div class="filter-group">
          <label for="country-filter">Filter by Country:</label>
          <select id="country-filter" v-model="countryFilter" @change="filterSkiSchools" class="filter-select">
            <option value="">All Countries</option>
            <option v-for="country in uniqueCountries" :key="country" :value="country">
              {{ country }}
            </option>
          </select>
        </div>
      </div>
      
      <div class="results-info">
        {{ filteredSkiSchools.length }} ski school{{ filteredSkiSchools.length !== 1 ? 's' : '' }} found
      </div>
    </div>

    <!-- Loading Spinner -->
    <LoadingSpinner v-if="loadingSkiSchools" />

    <!-- Ski Schools List -->
    <div v-else class="ski-schools-container">
      <div v-if="filteredSkiSchools.length === 0" class="no-schools-message">
        <i class="fas fa-skiing"></i>
        <h3>No ski schools found</h3>
        <p v-if="countryFilter">Try removing the country filter or create the first ski school in {{ countryFilter }}!</p>
        <p v-else>Be the first to create a ski school!</p>
      </div>
      
      <SkiSchoolContainer
        v-for="school in filteredSkiSchools"
        :key="school.id"
        :ski-school="school"
        :is-authenticated="isAuthenticated"
        :user-id="user?.id || null"
        @edit-school="editSkiSchool"
        @manage-coaches="manageCoaches"
      />
      
      <!-- Load More Button -->
      <div v-if="hasMoreSchools && !loadingMore" class="load-more-container">
        <button @click="loadMoreSchools" class="load-more-button">
          Load More Schools
        </button>
      </div>
      
      <LoadingSpinner v-if="loadingMore" />
    </div>

    <!-- Coach Management Modal -->
    <SkiCoachManagement
      :show="showCoachModal"
      :school="currentSchool"
      :user-id="user?.id || null"
      @close="closeCoachModal"
      @coach-added="onCoachAdded"
      @coach-removed="onCoachRemoved"
    />
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import SkiSchoolContainer from '@/components/SkiSchoolContainer.vue';
import SkiCoachManagement from '@/components/SkiCoachManagement.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import axios from 'axios';

export default {
  name: 'SkiSchools',
  components: {
    SkiSchoolContainer,
    SkiCoachManagement,
    LoadingSpinner
  },
  data() {
    return {
      skiSchools: [],
      filteredSkiSchools: [],
      loadingSkiSchools: true,
      loadingMore: false,
      hasMoreSchools: true,
      isAuthenticated: false,
      user: {},
      countryFilter: '',
      searchTerm: '',
      
      // Pagination
      limit: 10,
      offset: 0,

      showCoachModal: false,
      currentSchool: null
    }
  },
  computed: {
    uniqueCountries() {
      const countries = [...new Set(this.skiSchools.map(school => school.country))];
      return countries.sort();
    }
  },
  async created() {
    await this.checkAuthentication();
    await this.fetchSkiSchools();
  },
  methods: {
    async checkAuthentication() {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.user = response.data;
          this.isAuthenticated = true;
        } catch (error) {
          console.error("Error fetching user data:", error);
          this.isAuthenticated = false;
        }
      } else {
        this.isAuthenticated = false;
      }
    },

    async fetchSkiSchools(append = false) {
      try {
        if (!append) {
          this.loadingSkiSchools = true;
        } else {
          this.loadingMore = true;
        }

        const params = {
          limit: this.limit,
          offset: append ? this.offset : 0,
          is_active: true
        };

        if (this.countryFilter) {
          params.country = this.countryFilter;
        }

        const response = await axios.get(`${API_BASE_URL}/profiles/ski-schools/`, {
          params
        });

        if (append) {
          this.skiSchools = [...this.skiSchools, ...response.data];
        } else {
          this.skiSchools = response.data;
          this.offset = 0;
        }

        // Debug logging
        console.log('Current user:', this.user);
        console.log('User ID:', this.user.id);
        console.log('Is authenticated:', this.isAuthenticated);
        console.log('Ski schools data:', response.data);
        
        // Check each school's is_user_coach value
        response.data.forEach(school => {
          console.log(`School "${school.name}":`, {
            creator: school.creator,
            creator_profile: school.creator_profile,
            is_user_coach: school.is_user_coach,
            user_id_matches_creator: school.creator === this.user.id
          });
        });

        // Check if there are more schools to load
        this.hasMoreSchools = response.data.length === this.limit;
        
        // Update offset for next request
        this.offset += response.data.length;

        this.filterSkiSchools();
      } catch (error) {
        console.error('Error fetching ski schools:', error);
      } finally {
        this.loadingSkiSchools = false;
        this.loadingMore = false;
      }
    },

    async loadMoreSchools() {
      await this.fetchSkiSchools(true);
    },

    filterSkiSchools() {
      let filtered = [...this.skiSchools];
      
      // Apply country filter
      if (this.countryFilter) {
        filtered = filtered.filter(
          school => school.country.toLowerCase().includes(this.countryFilter.toLowerCase())
        );
      }
      
      // Apply search filter
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter(school => {
          return (
            school.name.toLowerCase().includes(searchLower) ||
            school.description.toLowerCase().includes(searchLower) ||
            school.country.toLowerCase().includes(searchLower) ||
            (school.address && school.address.toLowerCase().includes(searchLower)) ||
            (school.creator_profile?.first_name && school.creator_profile.first_name.toLowerCase().includes(searchLower)) ||
            (school.creator_profile?.last_name && school.creator_profile.last_name.toLowerCase().includes(searchLower)) ||
            (school.creator_profile?.username && school.creator_profile.username.toLowerCase().includes(searchLower))
          );
        });
      }
      
      this.filteredSkiSchools = filtered;
    },

    navigateToCreate() {
      this.$router.push('/ski-school/create');
    },

    editSkiSchool(school) {
      this.$router.push(`/ski-school/${school.slug}/edit`);
    },

    onCoachAdded(updatedSchool) {
      // Update the school in the local list
      const index = this.skiSchools.findIndex(school => school.id === this.currentSchool.id);
      if (index !== -1) {
        this.skiSchools[index] = updatedSchool;
        this.currentSchool = updatedSchool;
        this.filterSkiSchools();
      }
    },

    onCoachRemoved(updatedSchool) {
      // Update the school in the local list with the complete updated school data
      const index = this.skiSchools.findIndex(school => school.id === this.currentSchool.id);
      if (index !== -1) {
        this.skiSchools[index] = updatedSchool;
        this.currentSchool = updatedSchool;
        this.filterSkiSchools();
      }
    },

    closeCoachModal() {
      this.showCoachModal = false;
      this.currentSchool = null;
    },

    manageCoaches(school) {
      this.showCoachModal = true;
      this.currentSchool = school;
    }
  }
}
</script>

<style scoped>
.ski-schools-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #15202b;
  min-height: 100vh;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
  color: #d9d9d9;
}

.page-header h1 {
  color: #1da1f2;
  font-size: 2.5em;
  margin-bottom: 10px;
}

.page-description {
  color: #8899a6;
  font-size: 1.1em;
  margin-bottom: 20px;
}

.create-section {
  text-align: center;
  margin-bottom: 30px;
}

.create-school-button {
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 24px;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
}

.create-school-button:hover {
  background-color: #1a91da;
  transform: scale(1.05);
}

.create-school-button i {
  margin-right: 8px;
}

.filters-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background-color: #192734;
  padding: 20px;
  border-radius: 12px;
}

.filters-container {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-group label {
  color: #d9d9d9;
  font-weight: 500;
  white-space: nowrap;
}

.search-input {
  padding: 8px 12px;
  border: 1px solid #38444d;
  border-radius: 10px;
  background-color: #15202b;
  color: #d9d9d9;
  font-size: 16px;
  min-width: 200px;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #1da1f2;
}

.search-input::placeholder {
  color: #8899a6;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #38444d;
  border-radius: 10px;
  background-color: #15202b;
  color: #d9d9d9;
  cursor: pointer;
  font-size: 16px;
}

.results-info {
  color: #8899a6;
  font-size: 14px;
}

.ski-schools-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.no-schools-message {
  text-align: center;
  padding: 60px 20px;
  color: #8899a6;
}

.no-schools-message i {
  font-size: 4em;
  color: #2a3f54;
  margin-bottom: 20px;
}

.no-schools-message h3 {
  color: #d9d9d9;
  margin-bottom: 10px;
}

.load-more-container {
  text-align: center;
  margin-top: 30px;
}

.load-more-button {
  background-color: #2a3f54;
  color: #d9d9d9;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.load-more-button:hover {
  background-color: #375a7f;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .ski-schools-page {
    padding: 10px;
  }
  
  .filters-section {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .filters-container {
    flex-direction: column;
    gap: 15px;
    width: 100%;
  }
  
  .filter-group {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
  
  .search-input,
  .filter-select {
    width: 100%;
    min-width: unset;
  }
  
  .page-header h1 {
    font-size: 2em;
  }
}

@media (max-width: 600px) {
  .ski-schools-page {
    padding: 10px 4px 56px 4px;
  }
}
</style> 